import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, makeStyles } from '@material-ui/core'
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp'
import EditIcon from '@material-ui/icons/Edit'
import CheckIcon from '@material-ui/icons/Check'
import VisibilityIcon from '@material-ui/icons/Visibility'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import PresentationDetailPanel from '../../components/presentationManagement/PresentationDetailPanel'

const MONO = "'SF Mono', 'Fira Code', Menlo, Consolas, monospace"

const useStyles = makeStyles(() => ({
  card: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white',
    overflow: 'hidden',
    transition: 'border-color 0.15s ease',
    '&:hover': {
      borderColor: 'var(--color-neutral-300)'
    }
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-050)'
    }
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
    flexWrap: 'wrap'
  },
  title: {
    fontSize: '13.5px',
    fontWeight: 600,
    color: 'var(--color-neutral-900)'
  },
  code: {
    fontFamily: MONO,
    fontSize: '11px',
    color: 'var(--color-neutral-500)'
  },
  typeBadge: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    padding: '3px 8px',
    borderRadius: '3px',
    backgroundColor: 'var(--color-primary-050)',
    color: 'var(--color-primary-700)',
    fontFamily: MONO
  },
  wired: {
    fontFamily: MONO,
    fontSize: '11px',
    color: 'var(--color-neutral-500)'
  },
  wiredAccent: {
    color: 'var(--color-primary-700)',
    fontWeight: 600
  },
  summaryRow: {
    fontSize: '11.5px',
    color: 'var(--color-neutral-600)',
    fontFamily: MONO
  },
  expandIcon: {
    color: 'var(--color-neutral-400)',
    flexShrink: 0,
    fontSize: '20px'
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    flexShrink: 0
  },
  iconButton: {
    padding: '4px'
  },
  iconButtonActive: {
    color: 'var(--color-success-600)',
    backgroundColor: 'var(--color-success-050, #f0fdf4)'
  },
  iconButtonDanger: {
    color: 'var(--color-danger-500, #ef4444)',
    '&:hover': {
      backgroundColor: 'var(--color-danger-050, #fef2f2)'
    }
  },
  editingCard: {
    borderColor: 'var(--color-primary-300)',
    boxShadow: '0 0 0 3px var(--color-primary-050)'
  },
  body: {
    padding: '0 14px 14px',
    borderTop: '1px solid var(--color-neutral-100)'
  },
  editBody: {
    padding: '12px 14px 14px',
    borderTop: '1px solid var(--color-primary-200)',
    backgroundColor: 'var(--color-primary-050, #f0f7ff)'
  },
  section: {
    marginTop: '12px'
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--color-neutral-500)',
    marginBottom: '6px',
    fontFamily: MONO
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '4px 14px',
    fontSize: '12px',
    alignItems: 'baseline'
  },
  detailLabel: {
    fontFamily: MONO,
    fontSize: '11px',
    color: 'var(--color-neutral-500)'
  },
  detailValue: {
    color: 'var(--color-neutral-800)'
  },
  detailValueMono: {
    fontFamily: MONO,
    fontSize: '11.5px',
    color: 'var(--color-neutral-800)',
    wordBreak: 'break-all'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px'
  },
  th: {
    textAlign: 'left',
    padding: '6px 8px',
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-500)',
    borderBottom: '1px solid var(--color-neutral-200)',
    fontFamily: MONO
  },
  td: {
    padding: '6px 8px',
    borderBottom: '1px solid var(--color-neutral-100)',
    color: 'var(--color-neutral-800)'
  },
  tdMono: {
    fontFamily: MONO,
    fontSize: '11.5px'
  },
  bullet: {
    color: 'var(--color-neutral-400)',
    margin: '0 6px'
  },
  empty: {
    fontStyle: 'italic',
    color: 'var(--color-neutral-400)',
    fontSize: '11.5px'
  },
  jsonFallback: {
    fontFamily: MONO,
    fontSize: '11px',
    backgroundColor: 'var(--color-neutral-050)',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '4px',
    padding: '8px 10px',
    color: 'var(--color-neutral-700)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
    maxHeight: '200px',
    overflow: 'auto'
  }
}))

function dim (val) {
  return (val == null || val === '') ? '—' : val
}

// Metric icon is `{ type: 'library' | 'url', value }` per the schema. Older
// data may have stored a bare string. Always render to a string so React
// doesn't choke on the object.
function formatIcon (icon) {
  if (icon == null) return '—'
  if (typeof icon === 'string') return icon
  if (typeof icon === 'object' && icon.type && icon.value) {
    return `${icon.type}: ${icon.value}`
  }
  return '—'
}

// Metric footer is `{ template, format }`. Render as `template (format)` or
// just the template when no format is set.
function formatFooter (footer) {
  if (footer == null) return '—'
  if (typeof footer === 'string') return footer
  if (typeof footer === 'object' && footer.template) {
    return footer.format
      ? `${footer.template} · ${footer.format}`
      : footer.template
  }
  return '—'
}

function summaryFor (type, config) {
  if (!config) return null
  switch (type) {
    case 'Data Grid':
    case 'Presentation Table': {
      const cols = (config.columns || []).length
      const sort = (config.defaultSort || []).map(s => `${s.field} ${s.dir || 'asc'}`).join(', ')
      const parts = [`${cols} col${cols === 1 ? '' : 's'}`]
      if (sort) parts.push(`sort: ${sort}`)
      if (config.latchMode) parts.push(`latch: ${config.latchMode}`)
      if (config.topN) parts.push(`topN: ${config.topN}`)
      return parts
    }
    case 'Metric': {
      const parts = []
      if (config.accessor) parts.push(`accessor: ${config.accessor}`)
      if (config.primaryFormat) parts.push(`format: ${config.primaryFormat}`)
      if (config.size) parts.push(`size: ${config.size}`)
      return parts
    }
    case 'Chart': {
      const series = (config.series || []).length
      const parts = [`layout: ${config.layout || 'axis'}`]
      parts.push(`${series} series`)
      if (config.height) parts.push(`h: ${config.height}px`)
      return parts
    }
    case 'Hierarchical Table':
    case 'Hierarchical Pivot Table': {
      const levels = (config.hierarchy?.levels || []).length
      const cols = (config.columns || []).length
      const parts = [`${levels} level${levels === 1 ? '' : 's'}`, `${cols} col${cols === 1 ? '' : 's'}`]
      if (type === 'Hierarchical Pivot Table' && config.pivot?.pivotColumn) {
        parts.push(`pivot: ${config.pivot.pivotColumn}`)
      }
      if (type === 'Hierarchical Table' && config.hierarchy?.aggregation?.mode) {
        parts.push(`agg: ${config.hierarchy.aggregation.mode}`)
      }
      return parts
    }
    case 'Hierarchical Grid': {
      const levels = (config.hierarchy?.levels || []).length
      const cols = (config.columns || []).length
      const parts = [`${levels} level${levels === 1 ? '' : 's'}`, `${cols} col${cols === 1 ? '' : 's'}`]
      if (config.latchMode) parts.push(`latch: ${config.latchMode}`)
      return parts
    }
    case 'Scope Selector': {
      const scopes = (config.scopes || []).length
      const parts = [`mode: ${config.mode || 'membership'}`]
      if (scopes) parts.push(`${scopes} scope${scopes === 1 ? '' : 's'}`)
      if (config.multiSelect) parts.push('multi')
      return parts
    }
    default:
      return null
  }
}

function wiredTo (type, config) {
  if (!config) return null
  if (type === 'Hierarchical Grid') {
    const levels = config.hierarchy?.levels || []
    const codes = levels.map(l => l.purposeCode).filter(Boolean)
    if (codes.length === 0) return null
    if (codes.length === 1) return codes[0]
    return `${codes[0]} (+${codes.length - 1} more)`
  }
  return config.purposeCode || null
}

function ColumnsTable ({ columns }) {
  const classes = useStyles()
  if (!columns || columns.length === 0) {
    return <div className={classes.empty}>No columns configured</div>
  }
  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th className={classes.th}>Name</th>
          <th className={classes.th}>Display Name</th>
          <th className={classes.th}>Format</th>
          <th className={classes.th}>Width</th>
        </tr>
      </thead>
      <tbody>
        {columns.map((c, i) => (
          <tr key={c.name || i}>
            <td className={`${classes.td} ${classes.tdMono}`}>{dim(c.name)}</td>
            <td className={classes.td}>{dim(c.displayName)}</td>
            <td className={`${classes.td} ${classes.tdMono}`} style={{ color: 'var(--color-neutral-500)' }}>
              {dim(c.formatString)}
            </td>
            <td className={classes.td}>{dim(c.width)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

ColumnsTable.propTypes = { columns: PropTypes.array }

function HierarchyLevels ({ levels }) {
  const classes = useStyles()
  if (!levels || levels.length === 0) {
    return <div className={classes.empty}>No levels configured</div>
  }
  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th className={classes.th}>#</th>
          <th className={classes.th}>Purpose</th>
          <th className={classes.th}>Group by</th>
          <th className={classes.th}>Label</th>
        </tr>
      </thead>
      <tbody>
        {levels.map((lvl, i) => (
          <tr key={i}>
            <td className={`${classes.td} ${classes.tdMono}`}>{i}</td>
            <td className={`${classes.td} ${classes.tdMono}`} style={{ color: 'var(--color-primary-700)', fontWeight: 600 }}>
              {dim(lvl.purposeCode)}
            </td>
            <td className={`${classes.td} ${classes.tdMono}`}>
              {Array.isArray(lvl.groupBy) ? lvl.groupBy.join(', ') : dim(lvl.groupBy)}
            </td>
            <td className={`${classes.td} ${classes.tdMono}`}>{dim(lvl.labelAccessor || lvl.labelColumn)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

HierarchyLevels.propTypes = { levels: PropTypes.array }

function TypeDetail ({ type, config }) {
  const classes = useStyles()
  if (!config) return null

  switch (type) {
    case 'Data Grid':
    case 'Presentation Table':
      return (
        <>
          <div className={classes.section}>
            <div className={classes.sectionLabel}>Columns ({(config.columns || []).length})</div>
            <ColumnsTable columns={config.columns} />
          </div>
          <div className={classes.section}>
            <div className={classes.sectionLabel}>Settings</div>
            <div className={classes.detailGrid}>
              <span className={classes.detailLabel}>purpose</span>
              <span className={classes.detailValueMono}>{dim(config.purposeCode)}</span>
              <span className={classes.detailLabel}>default sort</span>
              <span className={classes.detailValueMono}>
                {(config.defaultSort || []).length === 0
                  ? '—'
                  : config.defaultSort.map(s => `${s.field} ${s.dir || 'asc'}`).join(', ')}
              </span>
              {config.latchMode && (
                <>
                  <span className={classes.detailLabel}>latch mode</span>
                  <span className={classes.detailValue}>{config.latchMode}</span>
                </>
              )}
              {config.localStorageKey && (
                <>
                  <span className={classes.detailLabel}>storage key</span>
                  <span className={classes.detailValueMono}>{config.localStorageKey}</span>
                </>
              )}
              {config.footerPurposeCode && (
                <>
                  <span className={classes.detailLabel}>footer purpose</span>
                  <span className={classes.detailValueMono}>{config.footerPurposeCode}</span>
                </>
              )}
            </div>
          </div>
        </>
      )

    case 'Metric':
      return (
        <div className={classes.section}>
          <div className={classes.sectionLabel}>Settings</div>
          <div className={classes.detailGrid}>
            <span className={classes.detailLabel}>purpose</span>
            <span className={classes.detailValueMono}>{dim(config.purposeCode)}</span>
            <span className={classes.detailLabel}>accessor</span>
            <span className={classes.detailValueMono}>{dim(config.accessor)}</span>
            <span className={classes.detailLabel}>title</span>
            <span className={classes.detailValue}>{dim(config.title)}</span>
            <span className={classes.detailLabel}>primary format</span>
            <span className={classes.detailValueMono}>{dim(config.primaryFormat)}</span>
            {(config.additionalFormats || []).length > 0 && (
              <>
                <span className={classes.detailLabel}>additional</span>
                <span className={classes.detailValueMono}>{config.additionalFormats.join(', ')}</span>
              </>
            )}
            <span className={classes.detailLabel}>size · align</span>
            <span className={classes.detailValue}>{config.size || 'md'} · {config.align || 'left'}</span>
            {config.icon && (
              <>
                <span className={classes.detailLabel}>icon</span>
                <span className={classes.detailValueMono}>{formatIcon(config.icon)}</span>
              </>
            )}
            {config.footer && (
              <>
                <span className={classes.detailLabel}>footer</span>
                <span className={classes.detailValue}>{formatFooter(config.footer)}</span>
              </>
            )}
          </div>
        </div>
      )

    case 'Chart':
      return (
        <>
          <div className={classes.section}>
            <div className={classes.sectionLabel}>Layout</div>
            <div className={classes.detailGrid}>
              <span className={classes.detailLabel}>purpose</span>
              <span className={classes.detailValueMono}>{dim(config.purposeCode)}</span>
              <span className={classes.detailLabel}>layout</span>
              <span className={classes.detailValue}>{config.layout || 'axis'}</span>
              <span className={classes.detailLabel}>height</span>
              <span className={classes.detailValue}>{config.height || 400}px</span>
              <span className={classes.detailLabel}>color theme</span>
              <span className={classes.detailValue}>{config.colorTheme || 'primary'}</span>
              {config.xAxis && (
                <>
                  <span className={classes.detailLabel}>x-axis</span>
                  <span className={classes.detailValueMono}>
                    {config.xAxis.type || 'category'} · {dim(config.xAxis.accessor)}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className={classes.section}>
            <div className={classes.sectionLabel}>Series ({(config.series || []).length})</div>
            {(config.series || []).length === 0 ? (
              <div className={classes.empty}>No series configured</div>
            ) : (
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>Type</th>
                    <th className={classes.th}>Accessor</th>
                    <th className={classes.th}>Name</th>
                    <th className={classes.th}>Axis</th>
                  </tr>
                </thead>
                <tbody>
                  {config.series.map((s, i) => (
                    <tr key={i}>
                      <td className={`${classes.td} ${classes.tdMono}`}>{dim(s.type)}</td>
                      <td className={`${classes.td} ${classes.tdMono}`}>{dim(s.accessor)}</td>
                      <td className={classes.td}>{dim(s.name)}</td>
                      <td className={`${classes.td} ${classes.tdMono}`}>{dim(s.axisId)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )

    case 'Hierarchical Table':
    case 'Hierarchical Pivot Table':
      return (
        <>
          <div className={classes.section}>
            <div className={classes.sectionLabel}>Hierarchy ({(config.hierarchy?.levels || []).length} levels)</div>
            <HierarchyLevels levels={config.hierarchy?.levels} />
          </div>
          {(config.columns || []).length > 0 && (
            <div className={classes.section}>
              <div className={classes.sectionLabel}>Columns ({config.columns.length})</div>
              <ColumnsTable columns={config.columns} />
            </div>
          )}
          <div className={classes.section}>
            <div className={classes.sectionLabel}>Settings</div>
            <div className={classes.detailGrid}>
              <span className={classes.detailLabel}>purpose</span>
              <span className={classes.detailValueMono}>{dim(config.purposeCode)}</span>
              <span className={classes.detailLabel}>expand depth</span>
              <span className={classes.detailValue}>{config.hierarchy?.defaultExpandDepth ?? 1}</span>
              {type === 'Hierarchical Table' && (
                <>
                  <span className={classes.detailLabel}>aggregation</span>
                  <span className={classes.detailValue}>{config.hierarchy?.aggregation?.mode || 'none'}</span>
                </>
              )}
              {type === 'Hierarchical Pivot Table' && config.pivot && (
                <>
                  <span className={classes.detailLabel}>pivot column</span>
                  <span className={classes.detailValueMono}>{dim(config.pivot.pivotColumn)}</span>
                  <span className={classes.detailLabel}>value column</span>
                  <span className={classes.detailValueMono}>{dim(config.pivot.valueColumn)}</span>
                  <span className={classes.detailLabel}>aggregate</span>
                  <span className={classes.detailValue}>{config.pivot.aggregateFunction || 'sum'}</span>
                </>
              )}
            </div>
          </div>
        </>
      )

    case 'Hierarchical Grid':
      return (
        <>
          <div className={classes.section}>
            <div className={classes.sectionLabel}>Hierarchy ({(config.hierarchy?.levels || []).length} levels)</div>
            <HierarchyLevels levels={config.hierarchy?.levels} />
          </div>
          {(config.columns || []).length > 0 && (
            <div className={classes.section}>
              <div className={classes.sectionLabel}>Columns ({config.columns.length})</div>
              <ColumnsTable columns={config.columns} />
            </div>
          )}
          <div className={classes.section}>
            <div className={classes.sectionLabel}>Settings</div>
            <div className={classes.detailGrid}>
              <span className={classes.detailLabel}>fetch depth</span>
              <span className={classes.detailValue}>{config.hierarchy?.initialFetchDepth ?? 1}</span>
              <span className={classes.detailLabel}>expand depth</span>
              <span className={classes.detailValue}>{config.hierarchy?.defaultExpandDepth ?? 0}</span>
              <span className={classes.detailLabel}>latch mode</span>
              <span className={classes.detailValue}>{config.latchMode || 'selectable'}</span>
              {config.localStorageKey && (
                <>
                  <span className={classes.detailLabel}>storage key</span>
                  <span className={classes.detailValueMono}>{config.localStorageKey}</span>
                </>
              )}
            </div>
          </div>
        </>
      )

    case 'Scope Selector':
      return (
        <div className={classes.section}>
          <div className={classes.sectionLabel}>Settings</div>
          <div className={classes.detailGrid}>
            <span className={classes.detailLabel}>mode</span>
            <span className={classes.detailValue}>{config.mode || 'membership'}</span>
            <span className={classes.detailLabel}>purpose</span>
            <span className={classes.detailValueMono}>{dim(config.purposeCode)}</span>
            <span className={classes.detailLabel}>display</span>
            <span className={classes.detailValue}>{config.display || 'simple'}</span>
            <span className={classes.detailLabel}>multi-select</span>
            <span className={classes.detailValue}>{config.multiSelect ? 'yes' : 'no'}</span>
            {(config.scopes || []).length > 0 && (
              <>
                <span className={classes.detailLabel}>scopes</span>
                <span className={classes.detailValueMono}>{config.scopes.join(', ')}</span>
              </>
            )}
          </div>
        </div>
      )

    default:
      return (
        <div className={classes.section}>
          <div className={classes.sectionLabel}>Config</div>
          <pre className={classes.jsonFallback}>{JSON.stringify(config, null, 2)}</pre>
        </div>
      )
  }
}

TypeDetail.propTypes = {
  type: PropTypes.string.isRequired,
  config: PropTypes.object
}

function PresentationOptionCard ({
  optionCode,
  option,
  allPurposes,
  defaultExpanded = false,
  onUpdateConfig,
  onUpdateOptionField,
  onDeleteOption,
  onPreview
}) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [editing, setEditing] = useState(false)

  const summary = summaryFor(option.type, option.config) || []
  const wired = wiredTo(option.type, option.config)

  const editable = Boolean(onUpdateConfig && onUpdateOptionField)

  const handleHeaderClick = () => {
    if (editing) return
    setExpanded(e => !e)
  }

  const handleToggleEdit = (e) => {
    e.stopPropagation()
    if (!editing) {
      setExpanded(true)
      setEditing(true)
    } else {
      setEditing(false)
    }
  }

  const handlePreview = (e) => {
    e.stopPropagation()
    if (onPreview) onPreview(optionCode)
  }

  const handleDelete = (e) => {
    e.stopPropagation()
    if (!onDeleteOption) return
    const ok = window.confirm(
      `Delete presentation option "${option.displayName || optionCode}"?`
    )
    if (ok) onDeleteOption(optionCode)
  }

  return (
    <div className={`${classes.card} ${editing ? classes.editingCard : ''}`}>
      <div
        className={classes.cardHeader}
        onClick={handleHeaderClick}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleHeaderClick() } }}
      >
        <div className={classes.titleBlock}>
          <div className={classes.titleRow}>
            <span className={classes.title}>{option.displayName || optionCode}</span>
            <span className={classes.code}>{optionCode}</span>
            <span className={classes.typeBadge}>{option.type || 'Unknown'}</span>
            {wired && (
              <span className={classes.wired}>
                wired to <span className={classes.wiredAccent}>{wired}</span>
              </span>
            )}
          </div>
          {summary.length > 0 && (
            <div className={classes.summaryRow}>
              {summary.map((s, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className={classes.bullet}>·</span>}
                  {s}
                </React.Fragment>
              ))}
            </div>
          )}
          {option.description && (
            <div style={{ fontSize: '11.5px', color: 'var(--color-neutral-600)' }}>
              {option.description}
            </div>
          )}
        </div>
        <div className={classes.headerButtons}>
          {onPreview && (
            <IconButton
              size='small'
              className={classes.iconButton}
              onClick={handlePreview}
              title='Preview presentation'
              aria-label='Preview presentation'
            >
              <VisibilityIcon fontSize='small' />
            </IconButton>
          )}
          {editable && (
            <IconButton
              size='small'
              className={`${classes.iconButton} ${editing ? classes.iconButtonActive : ''}`}
              onClick={handleToggleEdit}
              title={editing ? 'Done editing' : 'Edit presentation option'}
              aria-label={editing ? 'Done editing' : 'Edit presentation option'}
            >
              {editing ? <CheckIcon fontSize='small' /> : <EditIcon fontSize='small' />}
            </IconButton>
          )}
          {onDeleteOption && !editing && (
            <IconButton
              size='small'
              className={`${classes.iconButton} ${classes.iconButtonDanger}`}
              onClick={handleDelete}
              title='Delete presentation option'
              aria-label='Delete presentation option'
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          )}
          {!editing && (expanded
            ? <KeyboardArrowUp className={classes.expandIcon} />
            : <KeyboardArrowDown className={classes.expandIcon} />)}
        </div>
      </div>

      {editing && editable && (
        <div className={classes.editBody}>
          <PresentationDetailPanel
            optionCode={optionCode}
            optionData={option}
            allPurposes={allPurposes || {}}
            onUpdateConfig={onUpdateConfig}
            onUpdateOption={onUpdateOptionField}
          />
        </div>
      )}

      {expanded && !editing && (
        <div className={classes.body}>
          <TypeDetail type={option.type} config={option.config} />
        </div>
      )}
    </div>
  )
}

PresentationOptionCard.propTypes = {
  optionCode: PropTypes.string.isRequired,
  option: PropTypes.object.isRequired,
  allPurposes: PropTypes.object,
  defaultExpanded: PropTypes.bool,
  onUpdateConfig: PropTypes.func,
  onUpdateOptionField: PropTypes.func,
  onDeleteOption: PropTypes.func,
  onPreview: PropTypes.func
}

export default PresentationOptionCard
